import { DocumentStatus } from '../../payload/enums'
import { getDocs, getId, getObject } from '../../payload/payload.helpers'
import { PayloadIndividual } from '../../payload/payload-types.zod'
import z from 'zod'

import { DocumentDb, documentDbToResponse, DocumentResponseArraySchema } from './documents'
import { UserDb } from './users'

export const IndividualDbSchema = PayloadIndividual

export const RelationSchema = z.enum([
  'parent',
  'sibling',
  'grandparent',
  'personal_assistant',
  'foster_family',
  'housing_support',
  'self',
  'other',
])

export type Relation = z.infer<typeof RelationSchema>

// ─── Background regeneration status ────────────────────────────────────────────

// A run whose `startedAt` is older than this is considered stuck and may be
// re-dispatched (worker crashed before writing a terminal status).
export const REGEN_STUCK_MS = 30 * 60 * 1000
// Window used to dedupe near-simultaneous dispatches (e.g. a user click landing
// at the same time as a document finishing) into a single Cloud Task. Not a
// throttle — back-to-back regens in separate windows are allowed.
export const REGEN_DISPATCH_DEDUP_MS = 60 * 1000

export const RegenStatusSchema = z.object({
  status: z.enum(['idle', 'running', 'done', 'error']),
  stale: z.boolean(),
  staleSince: z.string().optional(),
  lastRegeneratedAt: z.string().optional(),
  error: z.string().optional(),
})

export type RegenStatus = z.infer<typeof RegenStatusSchema>

type RegenDb = z.infer<typeof IndividualDbSchema>['regen']

/**
 * True while a regen is genuinely in flight. A 'running' status older than
 * REGEN_STUCK_MS is treated as stuck (worker crashed before writing a terminal
 * status) so it no longer blocks a new dispatch. This is the only gate on
 * dispatch now that the cooldown has been removed.
 */
export function isRegenActivelyRunning(regen: RegenDb): boolean {
  if (regen?.status !== 'running') return false
  const startedMs = regen.startedAt ? Date.parse(regen.startedAt) : 0
  return Date.now() - startedMs < REGEN_STUCK_MS
}

/**
 * Derives the public regeneration status from the stored `regen` group. Used
 * both for the dispatch decision and the controller response.
 */
export function computeRegenStatus(regen: RegenDb): RegenStatus {
  const status = regen?.status ?? 'idle'

  return {
    status,
    stale: regen?.stale ?? false,
    staleSince: regen?.staleSince,
    lastRegeneratedAt: regen?.lastRegeneratedAt,
    // Only surface the error message while the run is actually in the error
    // state — avoids a stale message lingering after a later successful run.
    error: status === 'error' ? regen?.error : undefined,
  }
}

// ─── Subscription / entitlement ────────────────────────────────────────────────

// Max documents an individual on trial may hold at once. A live count of what exists
// right now, NOT a lifetime tally: deleting a document frees its slot immediately.
//
// That is a deliberate trade. It means the trial can be reused indefinitely by deleting
// and re-uploading, which a lifetime counter prevented. The counter's failure mode was
// worse: a trial user who deleted all three documents saw an empty list and could not
// upload anything ever again, with nothing in the UI to explain why.
export const TRIAL_DOCUMENT_LIMIT = 3
// Max trial-or-expired individuals one user may hold, created and invited-to
// combined. `active` individuals don't count, so paying for one frees a slot.
export const TRIAL_INDIVIDUAL_LIMIT = 2

/**
 * Whether a document occupies one of the trial's slots.
 *
 * Drafts do not: committing is what spends the budget, and until then the user is still
 * deciding what counts as one document. Errored documents do not either: they consumed no
 * AI work, and charging for a failure the user did not cause is the wrong answer.
 *
 * Everything in between — uploaded, parsing, analyzing, embedding, done — occupies a slot.
 */
export const occupiesDocumentSlot = (status?: string | null) =>
  status !== DocumentStatus.DRAFT && status !== DocumentStatus.ERROR

export const SubscriptionStatusSchema = z.object({
  status: z.enum(['trial', 'active', 'expired']),
  // Every mutation is blocked while read-only. Reads and file downloads still work.
  isReadOnly: z.boolean(),
  // Chat is the one feature a trial does not include at all — the trial is documents and the
  // Hitta stöd generation they produce, nothing else AI-driven. Separate from `isReadOnly`
  // because a trial is NOT read-only: it uploads, generates and edits freely, it just cannot
  // converse. Reading an existing conversation is still allowed; composing is what this gates.
  canUseChat: z.boolean(),
  // Errands are conversations: starting one creates a thread, so a trial that cannot
  // compose would end up with an errand it cannot use. Its own field rather than a reader
  // of `canUseChat`, so the two can part company — if errands ever join the trial, or chat
  // ever leaves it, this is the line that changes rather than every call site.
  canCreateErrands: z.boolean(),
  // Whether a payment provider is behind this, and so whether there is anything for the
  // customer portal to manage. Not the same question as "is it active": after the
  // backfill every pre-billing individual is active with no provider subscription at all,
  // and offering those a Manage button would send them to an endpoint that 404s.
  hasProviderBilling: z.boolean(),
  // When access runs out, so the client can say "renews on" for a subscription and "valid
  // until" for a period bought outright. Absent means open-ended, which is only ever a
  // manual grant.
  currentPeriodEnd: z.string().optional(),
  ownerId: z.string().optional(),
  documentsUsed: z.number(),
  // null means unlimited.
  documentLimit: z.number().nullable(),
  documentsRemaining: z.number().nullable(),
})

export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>

/**
 * An entitlement row as the access layer reads it, accepting both the zod-derived shape
 * (optional fields as `undefined`) and Payload's generated one (`| null`). Widening here
 * beats casting at each call site — a cast is exactly what would let a shape change
 * through unnoticed.
 */
export type EntitlementInput =
  | {
      source?: 'direct' | 'license' | 'manual' | null
      status?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | null
      currentPeriodEnd?: string | null
      owner?: string | { id: string } | null
    }
  | null
  | undefined

/**
 * Derives the public entitlement from an individual's `Entitlements` row.
 *
 * Decided against the CLOCK, not against the stored status. A period bought outright
 * produces no provider event when it lapses — nothing on Stripe's side tracks a year
 * somebody paid for once — so a stored status would read `active` forever. Anything whose
 * `currentPeriodEnd` has passed is expired regardless of what the row says.
 *
 * `past_due` deliberately keeps access. The customer has already paid for the period they
 * are in; a failed renewal ends it at `currentPeriodEnd` like anything else, which falls
 * out of the same rule for free.
 *
 * A missing row means the individual predates this collection, and is grandfathered as
 * 'active' rather than 'trial'. That deliberately fails open: locking existing users out
 * of documents they already uploaded would be worse than granting an unpaid individual
 * access until it is set explicitly. Worth tightening once the backfill is verified —
 * until then a failed backfill looks identical to everything working.
 */
export function computeSubscription(
  entitlement: EntitlementInput,
  options: { documentsUsed?: number; now?: number } = {},
): SubscriptionStatus {
  // Passed in rather than read off the entitlement row: the budget is a live count of the
  // individual's documents, which this function has no way to query. Callers that only ask
  // about status or read-only-ness leave it out — `documentLimit` is null for everyone but
  // a trial, so the number is unused for them.
  const { documentsUsed = 0, now = Date.now() } = options

  const stored = entitlement?.status ?? 'active'
  const periodEnd = entitlement?.currentPeriodEnd
    ? Date.parse(entitlement.currentPeriodEnd)
    : undefined

  // No end at all is open-ended, which is only ever correct for a grandfathered or
  // manually granted row. An unparseable date is treated as no date rather than as
  // expired: refusing access on a malformed field would be a worse failure than
  // granting it.
  const lapsed = periodEnd !== undefined && Number.isFinite(periodEnd) && periodEnd <= now

  const status: SubscriptionStatus['status'] =
    stored === 'canceled' || stored === 'expired' || lapsed
      ? 'expired'
      : stored === 'trialing'
        ? 'trial'
        : 'active'

  const documentLimit = status === 'trial' ? TRIAL_DOCUMENT_LIMIT : null

  return {
    status,
    isReadOnly: status === 'expired',
    // Only a paying subscription converses. A grandfathered row with no status resolves to
    // 'active' above, so existing users keep their chat.
    canUseChat: status === 'active',
    // The same answer as chat today, and deliberately written twice rather than derived
    // from it: they agree because an errand IS a conversation, not because one is defined
    // as the other.
    canCreateErrands: status === 'active',
    // 'license' is billed outside any provider and 'manual' was granted by hand, so
    // neither has a portal to open.
    hasProviderBilling: entitlement?.source === 'direct',
    currentPeriodEnd: entitlement?.currentPeriodEnd ?? undefined,
    ownerId: entitlement?.owner ? getId(entitlement.owner) : undefined,
    documentsUsed,
    documentLimit,
    documentsRemaining: documentLimit === null ? null : Math.max(0, documentLimit - documentsUsed),
  }
}

export const IndividualResponseSchema = IndividualDbSchema.omit({
  documents: true,
  // The admin's join onto the entitlement row. Internal — the client gets the computed
  // `subscription` below, and exposing the raw row as well would put two answers in one
  // response.
  entitlement: true,
  encryptedPid: true,
  generatedDescription: true,
  municipality: true,
  regen: true,
  // `subscription` is NOT omitted — the individual no longer has one to omit. It is added
  // by the extend below, computed from the individual's entitlement row, so the response
  // shape the client sees is unchanged.
  users: true,
}).extend({
  documents: DocumentResponseArraySchema,
  generatedDescription: z
    .object({
      short: z.string().optional(),
      detail: z.string().optional(),
    })
    .optional(),
  regen: RegenStatusSchema,
  subscription: SubscriptionStatusSchema,
  municipality: z.object({
    id: z.string(),
    links: z.object({
      domainUrl: z.string(),
      lssApplicationUrl: z.string().optional(),
      lssInformationUrl: z.string().optional(),
    }),
    name: z.string(),
  }),
  users: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      pid: z.string(),
      loggedIn: z.boolean(),
      roles: IndividualDbSchema.shape.users.element.shape.roles,
      relation: RelationSchema.default('parent'),
      relationOther: z.string().optional(),
      isGuardian: z.boolean().default(false),
      isLegalRepresentative: z.boolean().default(false),
    }),
  ),
})

export const CreateIndividualSchema = IndividualDbSchema.pick({
  firstName: true,
  gender: true,
  lastName: true,
  municipality: true,
  pid: true,
}).extend({
  creatorRelation: RelationSchema,
  creatorIsGuardian: z.boolean().default(false),
  creatorIsLegalRepresentative: z.boolean().default(false),
})

export const InviteUserSchema = z.object({
  pid: z.string().min(1),
  relation: RelationSchema,
  relationOther: z.string().optional(),
  isGuardian: z.boolean().default(false),
  isLegalRepresentative: z.boolean().default(false),
})

export const UpdateIndividualSchema = CreateIndividualSchema.omit({
  creatorRelation: true,
}).partial()

// Updates a user's relation (and guardian / legal-representative flags) to an
// individual. Targets the given `userId`, or the current user when omitted.
export const UpdateRelationSchema = z.object({
  userId: z.string().optional(),
  relation: RelationSchema,
  relationOther: z.string().optional(),
  isGuardian: z.boolean().optional(),
  isLegalRepresentative: z.boolean().optional(),
})

export type UpdateRelation = z.infer<typeof UpdateRelationSchema>

export const CreateIndividualDbSchema = IndividualDbSchema.pick({
  birthDate: true,
  encryptedPid: true,
  firstName: true,
  gender: true,
  lastName: true,
  municipality: true,
  pid: true,
  users: true,
  generatedDescription: true,
})

export type IndividualDb = z.infer<typeof IndividualDbSchema>

export type IndividualResponse = z.infer<typeof IndividualResponseSchema>

export type CreateIndividual = z.infer<typeof CreateIndividualSchema>

export type CreateIndividualDb = z.infer<typeof CreateIndividualDbSchema>

export type InviteUser = z.infer<typeof InviteUserSchema>

/**
 * `entitlement` is passed in rather than read off the individual: it lives in its own
 * collection now, and a response builder has no business issuing queries. Callers that
 * render a list fetch them all at once (`getEntitlementsByIndividuals`).
 */
export const individualDbToResponse = (data: IndividualDb, entitlement?: EntitlementInput) => {
  const documents = getDocs<DocumentDb>(data.documents)
  const municipality = getObject(data.municipality)!

  return IndividualResponseSchema.parse({
    birthDate: data.birthDate,
    createdAt: data.createdAt,
    documents: documents.map(documentDbToResponse),
    firstName: data.firstName,
    gender: data.gender,
    generatedDescription: data.generatedDescription
      ? {
          short: data.generatedDescription.short?.result,
          detail: data.generatedDescription.detail?.result,
        }
      : undefined,
    id: data.id,
    lastName: data.lastName,
    municipality,
    pid: data.pid,
    regen: computeRegenStatus(data.regen),
    // Counted from the documents this response already carries, so the live budget costs
    // no extra query here. Callers that do not load documents (the access layer) count
    // them themselves rather than silently reporting zero used.
    subscription: computeSubscription(entitlement, {
      documentsUsed: documents.filter(document => occupiesDocumentSlot(document.status)).length,
    }),
    updatedAt: data.updatedAt,
    users: data.users.map(data => {
      const user = getObject<UserDb>(data.user)!
      return {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        pid: user.pid,
        loggedIn: !!user.lastLogin,
        roles: data.roles,
        relation: data.relation ?? 'parent',
        relationOther: data.relationOther ?? undefined,
        isGuardian: data.isGuardian ?? false,
        isLegalRepresentative: data.isLegalRepresentative ?? false,
      }
    }),
  } satisfies IndividualResponse)
}
